/**
 * HederaHealthMetrics Component
 *
 * Collapsible panel showing Hedera blockchain health metrics:
 * - Network stats (nodes, TPS, accounts, transactions)
 * - Development activity (GitHub commits, stars)
 * - Overall health indicator (growing/stable/declining)
 *
 * Uses cached data from server (24h TTL) to reduce API calls.
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Icons } from '../Icons';
import {
  fetchNetworkHealth,
  refreshNetworkHealth,
  transformToGitHubStats,
  formatSupply,
  getTimeSinceUpdate,
  type BlockchainHealthData,
  type HederaNetworkMetrics,
  type GitHubStats,
  type TokenEmission,
} from '../../services/blockchainHealthApi';

type HealthStatus = 'growing' | 'stable' | 'declining' | 'loading';

const HEDERA_COLORS = {
  primary: '#8259EF',
  secondary: '#6366f1',
  accent: '#a855f7',
  gradient: 'linear-gradient(135deg, #8259EF, #6366f1)',
};

export function HederaHealthMetrics() {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [cachedData, setCachedData] = useState<BlockchainHealthData | null>(null);
  const [healthStatus, setHealthStatus] = useState<HealthStatus>('loading');

  // Derived data from cache
  const githubStats: GitHubStats | null = cachedData ? transformToGitHubStats(cachedData) : null;
  const networkMetrics: HederaNetworkMetrics | null = cachedData?.network_metrics as HederaNetworkMetrics | null;
  const nextEmissions: TokenEmission[] = cachedData?.token_emissions || [];

  // Load data from cache API
  const loadData = async (forceRefresh = false) => {
    setIsLoading(true);

    try {
      const data = forceRefresh
        ? await refreshNetworkHealth('hedera')
        : await fetchNetworkHealth('hedera');

      if (data) {
        setCachedData(data);
        setHealthStatus(data.health_status as HealthStatus);
      }
    } catch (error) {
      console.error('[HederaHealth] Error loading data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Load data when expanded
  useEffect(() => {
    if (isExpanded && !cachedData && !isLoading) {
      loadData();
    }
  }, [isExpanded]);

  // Health status styling
  const getHealthStyle = (status: HealthStatus) => {
    switch (status) {
      case 'growing':
        return { color: '#10b981', bg: 'rgba(16, 185, 129, 0.15)', icon: Icons.TrendingUp, label: t('hedera.health.growing', 'En croissance') };
      case 'stable':
        return { color: '#f59e0b', bg: 'rgba(245, 158, 11, 0.15)', icon: Icons.Activity, label: t('hedera.health.stable', 'Stable') };
      case 'declining':
        return { color: '#ef4444', bg: 'rgba(239, 68, 68, 0.15)', icon: Icons.TrendingDown, label: t('hedera.health.declining', 'En baisse') };
      default:
        return { color: 'var(--muted)', bg: 'var(--border)', icon: Icons.Loader2, label: t('hedera.health.loading', 'Chargement...') };
    }
  };

  const healthStyle = getHealthStyle(healthStatus);
  const HealthIcon = healthStyle.icon;

  // Format date
  const formatDate = (dateStr: string) => {
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });
    } catch {
      return dateStr;
    }
  };

  return (
    <div style={{
      background: 'rgba(0, 0, 0, 0.25)',
      backdropFilter: 'blur(20px)',
      borderRadius: '16px',
      border: '1px solid rgba(255, 255, 255, 0.08)',
      marginBottom: '20px',
      overflow: 'hidden'
    }}>
      {/* Collapsible Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        style={{
          width: '100%',
          padding: '16px 20px',
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          color: 'var(--text)'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{
            padding: '8px',
            background: HEDERA_COLORS.gradient,
            borderRadius: '10px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <Icons.Activity size={20} style={{ color: 'white' }} />
          </div>
          <div style={{ textAlign: 'left' }}>
            <h3 style={{ fontSize: '16px', fontWeight: 600, margin: 0 }}>
              {t('hedera.health.title', 'Santé du Réseau Hedera')}
            </h3>
            <p style={{ fontSize: '12px', color: 'var(--muted)', margin: '4px 0 0 0' }}>
              {t('hedera.health.subtitle', 'Métriques réseau et activité développement')}
            </p>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {/* Health Status Badge */}
          <div style={{
            padding: '6px 12px',
            background: healthStyle.bg,
            color: healthStyle.color,
            borderRadius: '20px',
            fontSize: '13px',
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center',
            gap: '6px'
          }}>
            <HealthIcon size={14} style={healthStatus === 'loading' ? { animation: 'spin 1s linear infinite' } : {}} />
            {healthStyle.label}
          </div>

          {/* Expand/Collapse Arrow */}
          <Icons.ChevronDown
            size={20}
            style={{
              transition: 'transform 0.2s',
              transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)'
            }}
          />
        </div>
      </button>

      {/* Expanded Content */}
      {isExpanded && (
        <div style={{
          padding: '0 20px 20px 20px',
          borderTop: '1px solid rgba(255, 255, 255, 0.08)'
        }}>
          {isLoading ? (
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              padding: '40px',
              color: 'var(--muted)'
            }}>
              <Icons.Loader2 size={24} style={{ animation: 'spin 1s linear infinite', marginRight: '12px' }} />
              {t('hedera.health.loading', 'Chargement des métriques...')}
            </div>
          ) : (
            <>
              {/* Network Metrics Grid */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))',
                gap: '12px',
                marginTop: '16px'
              }}>
                {networkMetrics && (
                  <>
                    {/* Consensus Nodes */}
                    <div style={{
                      padding: '16px',
                      background: 'rgba(130, 89, 239, 0.1)',
                      border: '1px solid rgba(130, 89, 239, 0.2)',
                      borderRadius: '12px',
                      textAlign: 'center'
                    }}>
                      <Icons.Server size={24} style={{ color: HEDERA_COLORS.primary, marginBottom: '8px' }} />
                      <div style={{ fontSize: '24px', fontWeight: 700, color: HEDERA_COLORS.primary }}>
                        {networkMetrics.totalNodes ?? 0}
                      </div>
                      <div style={{ fontSize: '11px', color: 'var(--muted)', marginTop: '4px' }}>
                        {t('hedera.health.nodes', 'Nodes Consensus')}
                      </div>
                    </div>

                    {/* TPS */}
                    <div style={{
                      padding: '16px',
                      background: 'rgba(16, 185, 129, 0.1)',
                      border: '1px solid rgba(16, 185, 129, 0.2)',
                      borderRadius: '12px',
                      textAlign: 'center'
                    }}>
                      <Icons.Zap size={24} style={{ color: '#10b981', marginBottom: '8px' }} />
                      <div style={{ fontSize: '24px', fontWeight: 700, color: '#10b981' }}>
                        {networkMetrics.tps ?? 0}
                      </div>
                      <div style={{ fontSize: '11px', color: 'var(--muted)', marginTop: '4px' }}>
                        TPS (avg)
                      </div>
                    </div>

                    {/* Total Accounts */}
                    <div style={{
                      padding: '16px',
                      background: 'rgba(99, 102, 241, 0.1)',
                      border: '1px solid rgba(99, 102, 241, 0.2)',
                      borderRadius: '12px',
                      textAlign: 'center'
                    }}>
                      <Icons.Users size={24} style={{ color: '#6366f1', marginBottom: '8px' }} />
                      <div style={{ fontSize: '24px', fontWeight: 700, color: '#6366f1' }}>
                        {networkMetrics.totalAccounts ?? '0'}
                      </div>
                      <div style={{ fontSize: '11px', color: 'var(--muted)', marginTop: '4px' }}>
                        {t('hedera.health.accounts', 'Comptes')}
                      </div>
                    </div>

                    {/* Total Transactions */}
                    <div style={{
                      padding: '16px',
                      background: 'rgba(168, 85, 247, 0.1)',
                      border: '1px solid rgba(168, 85, 247, 0.2)',
                      borderRadius: '12px',
                      textAlign: 'center'
                    }}>
                      <Icons.Activity size={24} style={{ color: '#a855f7', marginBottom: '8px' }} />
                      <div style={{ fontSize: '24px', fontWeight: 700, color: '#a855f7' }}>
                        {networkMetrics.totalTransactions ?? '0'}
                      </div>
                      <div style={{ fontSize: '11px', color: 'var(--muted)', marginTop: '4px' }}>
                        {t('hedera.health.transactions', 'Transactions')}
                      </div>
                    </div>

                    {/* Total Supply */}
                    <div style={{
                      padding: '16px',
                      background: 'rgba(245, 158, 11, 0.1)',
                      border: '1px solid rgba(245, 158, 11, 0.2)',
                      borderRadius: '12px',
                      textAlign: 'center'
                    }}>
                      <Icons.Database size={24} style={{ color: '#f59e0b', marginBottom: '8px' }} />
                      <div style={{ fontSize: '24px', fontWeight: 700, color: '#f59e0b' }}>
                        {networkMetrics.totalSupply ?? '0'}
                      </div>
                      <div style={{ fontSize: '11px', color: 'var(--muted)', marginTop: '4px' }}>
                        HBAR Supply
                      </div>
                    </div>

                    {/* Staking APY */}
                    <div style={{
                      padding: '16px',
                      background: 'rgba(34, 197, 94, 0.1)',
                      border: '1px solid rgba(34, 197, 94, 0.2)',
                      borderRadius: '12px',
                      textAlign: 'center'
                    }}>
                      <Icons.Percent size={24} style={{ color: '#22c55e', marginBottom: '8px' }} />
                      <div style={{ fontSize: '24px', fontWeight: 700, color: '#22c55e' }}>
                        ~{networkMetrics.stakingRewardRate ?? 0}%
                      </div>
                      <div style={{ fontSize: '11px', color: 'var(--muted)', marginTop: '4px' }}>
                        Staking APY
                      </div>
                    </div>
                  </>
                )}

                {/* Supply Metrics from cached data */}
                {cachedData && (
                  <>
                    {/* Circulating Supply */}
                    <div style={{
                      padding: '16px',
                      background: 'rgba(139, 92, 246, 0.1)',
                      border: '1px solid rgba(139, 92, 246, 0.2)',
                      borderRadius: '12px',
                      textAlign: 'center'
                    }}>
                      <Icons.Coins size={24} style={{ color: '#8b5cf6', marginBottom: '8px' }} />
                      <div style={{ fontSize: '24px', fontWeight: 700, color: '#8b5cf6' }}>
                        {formatSupply(cachedData.circulating_supply)}
                      </div>
                      <div style={{ fontSize: '11px', color: 'var(--muted)', marginTop: '4px' }}>
                        {t('hedera.health.circulating', 'En Circulation')}
                      </div>
                    </div>

                    {/* Supply Percentage */}
                    <div style={{
                      padding: '16px',
                      background: 'rgba(236, 72, 153, 0.1)',
                      border: '1px solid rgba(236, 72, 153, 0.2)',
                      borderRadius: '12px',
                      textAlign: 'center'
                    }}>
                      <Icons.PieChart size={24} style={{ color: '#ec4899', marginBottom: '8px' }} />
                      <div style={{ fontSize: '24px', fontWeight: 700, color: '#ec4899' }}>
                        {cachedData.circulating_percent ?? 0}%
                      </div>
                      <div style={{ fontSize: '11px', color: 'var(--muted)', marginTop: '4px' }}>
                        {t('hedera.health.supplyPercent', 'du Max Supply')}
                      </div>
                    </div>
                  </>
                )}
              </div>

              {/* Token Emissions Section */}
              {nextEmissions.length > 0 && (
                <div style={{ marginTop: '16px' }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    marginBottom: '10px'
                  }}>
                    <Icons.Calendar size={16} style={{ color: 'var(--muted)' }} />
                    <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--muted)' }}>
                      {t('hedera.health.nextEmissions', 'Prochaines Émissions')}
                    </span>
                  </div>
                  <div style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '8px'
                  }}>
                    {nextEmissions.map((emission, idx) => (
                      <div key={idx} style={{
                        padding: '10px 14px',
                        background: 'rgba(130, 89, 239, 0.1)',
                        border: '1px solid rgba(130, 89, 239, 0.3)',
                        borderRadius: '8px',
                        fontSize: '12px'
                      }}>
                        <div style={{ fontWeight: 600, color: HEDERA_COLORS.primary }}>{emission.date}</div>
                        <div style={{ color: 'var(--muted)', marginTop: '2px' }}>{emission.amount}</div>
                        <div style={{ color: 'var(--muted)', fontSize: '10px' }}>{emission.type}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* GitHub Development Section */}
              {githubStats && (
                <div style={{ marginTop: '20px' }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    marginBottom: '12px'
                  }}>
                    <Icons.Code size={16} style={{ color: 'var(--muted)' }} />
                    <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--muted)' }}>
                      {t('hedera.health.development', 'Activité Développement')} (GitHub)
                    </span>
                    <a
                      href="https://github.com/hashgraph/hedera-services"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        marginLeft: 'auto',
                        fontSize: '12px',
                        color: HEDERA_COLORS.primary,
                        textDecoration: 'none',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}
                    >
                      <Icons.ExternalLink size={12} />
                      hashgraph/hedera-services
                    </a>
                  </div>

                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))',
                    gap: '10px'
                  }}>
                    {/* Stars */}
                    <div style={{
                      padding: '12px',
                      background: 'rgba(130, 89, 239, 0.1)',
                      border: '1px solid rgba(130, 89, 239, 0.15)',
                      borderRadius: '10px',
                      textAlign: 'center'
                    }}>
                      <Icons.Star size={18} style={{ color: '#fbbf24', marginBottom: '6px' }} />
                      <div style={{ fontSize: '18px', fontWeight: 700 }}>{githubStats.stars ?? 0}</div>
                      <div style={{ fontSize: '10px', color: 'var(--muted)' }}>Stars</div>
                    </div>

                    {/* Forks */}
                    <div style={{
                      padding: '12px',
                      background: 'rgba(130, 89, 239, 0.1)',
                      border: '1px solid rgba(130, 89, 239, 0.15)',
                      borderRadius: '10px',
                      textAlign: 'center'
                    }}>
                      <Icons.GitMerge size={18} style={{ color: '#a78bfa', marginBottom: '6px' }} />
                      <div style={{ fontSize: '18px', fontWeight: 700 }}>{githubStats.forks ?? 0}</div>
                      <div style={{ fontSize: '10px', color: 'var(--muted)' }}>Forks</div>
                    </div>

                    {/* Open Issues */}
                    <div style={{
                      padding: '12px',
                      background: 'rgba(130, 89, 239, 0.1)',
                      border: '1px solid rgba(130, 89, 239, 0.15)',
                      borderRadius: '10px',
                      textAlign: 'center'
                    }}>
                      <Icons.AlertCircle size={18} style={{ color: '#f97316', marginBottom: '6px' }} />
                      <div style={{ fontSize: '18px', fontWeight: 700 }}>{(githubStats.openIssues ?? 0).toLocaleString()}</div>
                      <div style={{ fontSize: '10px', color: 'var(--muted)' }}>Issues</div>
                    </div>

                    {/* Recent Commits */}
                    <div style={{
                      padding: '12px',
                      background: 'rgba(130, 89, 239, 0.1)',
                      border: '1px solid rgba(130, 89, 239, 0.15)',
                      borderRadius: '10px',
                      textAlign: 'center'
                    }}>
                      <Icons.GitCompare size={18} style={{ color: '#22c55e', marginBottom: '6px' }} />
                      <div style={{ fontSize: '18px', fontWeight: 700 }}>~{githubStats.recentCommits ?? 0}</div>
                      <div style={{ fontSize: '10px', color: 'var(--muted)' }}>Commits/30j</div>
                    </div>

                    {/* Last Commit */}
                    <div style={{
                      padding: '12px',
                      background: 'rgba(130, 89, 239, 0.1)',
                      border: '1px solid rgba(130, 89, 239, 0.15)',
                      borderRadius: '10px',
                      textAlign: 'center',
                      gridColumn: 'span 2'
                    }}>
                      <Icons.Clock size={18} style={{ color: '#64748b', marginBottom: '6px' }} />
                      <div style={{ fontSize: '14px', fontWeight: 600 }}>{formatDate(githubStats.lastCommitDate)}</div>
                      <div style={{ fontSize: '10px', color: 'var(--muted)' }}>
                        {t('hedera.health.lastCommit', 'Dernier commit')}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Health Explanation */}
              <div style={{
                marginTop: '20px',
                padding: '14px',
                background: healthStyle.bg,
                borderRadius: '10px',
                borderLeft: `4px solid ${healthStyle.color}`
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <HealthIcon size={20} style={{ color: healthStyle.color }} />
                  <div>
                    <div style={{ fontSize: '14px', fontWeight: 600, color: healthStyle.color }}>
                      {t('hedera.health.status', 'État')}: {healthStyle.label}
                    </div>
                    <div style={{ fontSize: '12px', color: 'var(--muted)', marginTop: '4px' }}>
                      {healthStatus === 'growing' && t('hedera.health.growingDesc', 'Le réseau Hedera montre une activité de développement soutenue et une adoption croissante.')}
                      {healthStatus === 'stable' && t('hedera.health.stableDesc', 'Le réseau maintient une activité régulière avec un développement continu.')}
                      {healthStatus === 'declining' && t('hedera.health.decliningDesc', 'L\'activité du réseau montre des signes de ralentissement.')}
                    </div>
                  </div>
                </div>
              </div>

              {/* Refresh Button */}
              <div style={{
                marginTop: '16px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                {cachedData && (
                  <span style={{ fontSize: '11px', color: 'var(--muted)' }}>
                    {t('hedera.health.lastUpdate', 'Dernière mise à jour')}: {getTimeSinceUpdate(cachedData)}
                  </span>
                )}
                <button
                  onClick={() => loadData(true)}
                  disabled={isLoading}
                  style={{
                    padding: '8px 16px',
                    fontSize: '12px',
                    fontWeight: 600,
                    background: 'rgba(130, 89, 239, 0.15)',
                    color: 'var(--text)',
                    border: '1px solid rgba(130, 89, 239, 0.3)',
                    borderRadius: '8px',
                    cursor: isLoading ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    opacity: isLoading ? 0.5 : 1
                  }}
                >
                  <Icons.RefreshCw size={14} style={isLoading ? { animation: 'spin 1s linear infinite' } : {}} />
                  {t('hedera.health.refresh', 'Actualiser')}
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
